import { Button, ButtonLink } from '@postscript/components';
import CreateFlowModal from 'components/flowBuilder/components/modals/CreateFlowModal';
import { FlowTypes } from 'components/flowBuilder/constants';
import { MigrationPreparation } from 'components/ProgressiveOnboarding/MigrationPreparation';
import { TryItYourself } from 'components/ProgressiveOnboarding/TryItYourself';
import { useUser } from 'controllers/contexts/user';
import {
  AUTOMATION_ACTIVATED_MANAGED_STEP_KEY,
  AUTOMATION_CONFIG_BASE_PATH,
  BUILD_CAMPAIGN_PATH,
  CAMPAIGN_SENT_MANAGED_STEP_KEY,
  POPUP_ACTIVATED_MANAGED_STEP_KEY,
  POPUP_CONFIG_BASE_PATH,
  POPUP_REVIEW_BASE_PATH,
  PROGRESSIVE_ONBOARDING_SOURCE,
  PS_APP_PROG_ONBOARDING_FEATURE_KEY,
  SUBSCRIBERS_IMPORTED_MANAGED_STEP_KEY,
  SUBSCRIBER_LIST_PATH,
} from 'providers/ProgressiveOnboarding/constants/constants';
import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';
import type { WorkflowContent } from 'providers/ProgressiveOnboarding/types';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { logButtonClickEvent, PRODUCT_AREAS } from 'utils/events';

const AutomationsManagedContent = () => {
  const [createFlowModalIsOpen, setCreateFlowModalIsOpen] = useState(false);
  const { setActiveGuide } = useProgressiveOnboarding();

  return (
    <>
      Automated flows message subscribers at key points in their journey, such
      as cart additions, first purchases, or SMS signup.
      <TryItYourself
        productArea={PRODUCT_AREAS.AUTOMATIONS}
        workflow={PS_APP_PROG_ONBOARDING_FEATURE_KEY}
        step={AUTOMATION_ACTIVATED_MANAGED_STEP_KEY}
        message="Build an automation flow to master the process."
        onTryClick={() => {
          setCreateFlowModalIsOpen(true);
        }}
      />
      <CreateFlowModal
        flowType={FlowTypes.AUTOMATION}
        isOpen={createFlowModalIsOpen}
        onCancel={() => {
          setCreateFlowModalIsOpen(false);
          setActiveGuide(undefined);
        }}
      />
    </>
  );
};

export const ACTIVATE_AUTOMATION_MANAGED_STEP_CONTENT: WorkflowContent['steps'] =
  {
    [AUTOMATION_ACTIVATED_MANAGED_STEP_KEY]: {
      nodeAbove: <MigrationPreparation />,
      key: AUTOMATION_ACTIVATED_MANAGED_STEP_KEY,
      guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
      title: 'Review Your Automations',
      content: <AutomationsManagedContent />,
      icon: 'Disc',
      PrimaryAction: () => {
        const { user } = useUser();

        return (
          <ButtonLink
            to={AUTOMATION_CONFIG_BASE_PATH}
            onClick={(event) => {
              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.AUTOMATIONS,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                step: AUTOMATION_ACTIVATED_MANAGED_STEP_KEY,
                shop_id: user.shop_id,
              });
            }}
          >
            Review Automations
          </ButtonLink>
        );
      },
      SecondaryAction: ({ step }) => {
        const { markStepCompleted } = useProgressiveOnboarding();
        const { user } = useUser();

        if (step.state === 'complete') {
          return null;
        }

        return (
          <Button
            variant="text"
            onClick={(event) => {
              markStepCompleted({
                guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                stepKey: AUTOMATION_ACTIVATED_MANAGED_STEP_KEY,
              });

              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.AUTOMATIONS,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                step: AUTOMATION_ACTIVATED_MANAGED_STEP_KEY,
                shop_id: user.shop_id,
              });
            }}
          >
            Mark as Complete
          </Button>
        );
      },
    },
  };

const ActivateCampaignManagedContent = () => {
  const [createFlowModalIsOpen, setCreateFlowModalIsOpen] = useState(false);

  return (
    <>
      Use campaigns to schedule and send messages to a specific customer segment
      or your entire customer base.
      <br />
      <br />
      Campaigns are ideal for new product launches or announcements such as a{' '}
      <b>New Number Campaign.</b>
      <TryItYourself
        productArea={PRODUCT_AREAS.CAMPAIGNS}
        workflow={PS_APP_PROG_ONBOARDING_FEATURE_KEY}
        step={CAMPAIGN_SENT_MANAGED_STEP_KEY}
        message="Create a campaign to learn how it works."
        onTryClick={() => {
          setCreateFlowModalIsOpen(true);
        }}
      />
      <CreateFlowModal
        flowType={FlowTypes.CAMPAIGN}
        isOpen={createFlowModalIsOpen}
        onCancel={() => {
          setCreateFlowModalIsOpen(false);
        }}
      />
    </>
  );
};

export const ACTIVATE_CAMPAIGN_MANAGED_STEP_CONTENT: WorkflowContent['steps'] =
  {
    [CAMPAIGN_SENT_MANAGED_STEP_KEY]: {
      key: CAMPAIGN_SENT_MANAGED_STEP_KEY,
      guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
      title: 'Review Your Campaign',
      content: <ActivateCampaignManagedContent />,
      icon: 'Disc',
      PrimaryAction: () => {
        const { user } = useUser();

        return (
          <ButtonLink
            to={BUILD_CAMPAIGN_PATH}
            onClick={(event) => {
              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.CAMPAIGNS,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                step: CAMPAIGN_SENT_MANAGED_STEP_KEY,
                shop_id: user.shop_id,
              });
            }}
          >
            Review Campaigns
          </ButtonLink>
        );
      },
      SecondaryAction: ({ step }) => {
        const { markStepCompleted } = useProgressiveOnboarding();
        const { user } = useUser();

        if (step.state === 'complete') {
          return null;
        }

        return (
          <Button
            variant="text"
            onClick={(event) => {
              markStepCompleted({
                guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                stepKey: CAMPAIGN_SENT_MANAGED_STEP_KEY,
              });

              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.CAMPAIGNS,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                step: CAMPAIGN_SENT_MANAGED_STEP_KEY,
                shop_id: user.shop_id,
              });
            }}
          >
            Mark as Complete
          </Button>
        );
      },
    },
  };

const ActivatePopupManagedContent = () => {
  const history = useHistory();

  return (
    <>
      Postscript&quot;s new popup editor grows your list and drives revenue with
      built-in compliance, flexible segmenting, and Onsite Opt-in (delivers up
      to 3x more subscribers).
      <TryItYourself
        productArea={PRODUCT_AREAS.POPUPS}
        workflow={PS_APP_PROG_ONBOARDING_FEATURE_KEY}
        step={POPUP_ACTIVATED_MANAGED_STEP_KEY}
        message="Explore the new popup editor to learn how it works."
        onTryClick={() => {
          history.push(POPUP_CONFIG_BASE_PATH);
        }}
      />
    </>
  );
};

export const ACTIVATE_POPUP_MANAGED_STEP_CONTENT: WorkflowContent['steps'] = {
  [POPUP_ACTIVATED_MANAGED_STEP_KEY]: {
    key: POPUP_ACTIVATED_MANAGED_STEP_KEY,
    guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
    title: 'Review Your Popups',
    content: <ActivatePopupManagedContent />,
    icon: 'Disc',
    PrimaryAction: () => {
      const { user } = useUser();

      return (
        <ButtonLink
          to={POPUP_REVIEW_BASE_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.POPUPS,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: POPUP_ACTIVATED_MANAGED_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Review Popups
        </ButtonLink>
      );
    },
    SecondaryAction: ({ step }) => {
      const { markStepCompleted } = useProgressiveOnboarding();
      const { user } = useUser();

      if (step.state === 'complete') {
        return null;
      }

      return (
        <Button
          variant="text"
          onClick={(event) => {
            markStepCompleted({
              guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              stepKey: POPUP_ACTIVATED_MANAGED_STEP_KEY,
            });

            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.POPUPS,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: POPUP_ACTIVATED_MANAGED_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Mark as Complete
        </Button>
      );
    },
  },
};

export const IMPORT_SUBSCRIBERS_MANAGED_STEP_CONTENT: WorkflowContent['steps'] =
  {
    [SUBSCRIBERS_IMPORTED_MANAGED_STEP_KEY]: {
      key: SUBSCRIBERS_IMPORTED_MANAGED_STEP_KEY,
      guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
      title: 'Import Subscribers',
      content: (
        <>
          Migrate your existing subscriber list to Postscript.
          <br />
          <br />
          1. Disable all subscriber collection tools and messaging on your
          previous platform.
          <br />
          <br />
          2. Complete the &quot;Create Upload Request&quot; form and upload your
          CSV file.
          <br />
          <br />
          3. Review the subscriber counts to confirm they are accurate.
          <br />
          <br />
          4. After submission, wait for approval (typically 1–3 business days).
        </>
      ),
      icon: 'Disc',
      PrimaryAction: () => {
        const { user } = useUser();

        return (
          <ButtonLink
            to={SUBSCRIBER_LIST_PATH}
            onClick={(event) => {
              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.SUBSCRIBERS,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                step: SUBSCRIBERS_IMPORTED_MANAGED_STEP_KEY,
                shop_id: user.shop_id,
              });
            }}
          >
            Go to Subscriber List
          </ButtonLink>
        );
      },
      SecondaryAction: ({ step }) => {
        const { markStepCompleted } = useProgressiveOnboarding();
        const { user } = useUser();

        if (step.state === 'complete') {
          return null;
        }

        return (
          <Button
            variant="text"
            onClick={(event) => {
              markStepCompleted({
                guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                stepKey: SUBSCRIBERS_IMPORTED_MANAGED_STEP_KEY,
              });

              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.SUBSCRIBERS,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                step: SUBSCRIBERS_IMPORTED_MANAGED_STEP_KEY,
                shop_id: user.shop_id,
              });
            }}
          >
            Mark as Complete
          </Button>
        );
      },
      badge: {
        text: 'For Migration Only',
        variant: 'info',
      },
    },
  };
